import type { Agency } from "../types";

/**
 * PT-100 / B4.1: an internal agency is an Echoflicks studio: one of our test
 * studios, or a prospect studio staff set up. What the flag does, in the words
 * the Agencies tab uses (plan 2026-09-26 §3.1, B4.3):
 */
export const INTERNAL_FLAG_EFFECT =
  "its films count as ours, not the customer's, and its alerts are tagged internal";

/** A row read before the column exists (migration not applied) is a customer. */
export const isInternalAgency = (agency: Pick<Agency, "is_internal">) =>
  agency.is_internal === true;

/**
 * - `customer`: the client's studio (never decided, handed over, or set so by hand);
 * - `prospect`: internal because staff set it up; the database turns it
 *   external on its client's first sign-in (`internal_source = 'auto'`);
 * - `internal`: set internal by a person (`internal_source = 'manual'`, or a
 *   row whose source predates 20260926120000_agency_internal_auto).
 */
export type AgencyInternalState = "customer" | "prospect" | "internal";

type InternalFields = Pick<Agency, "is_internal" | "internal_source">;

export const agencyInternalState = (
  agency: InternalFields,
): AgencyInternalState => {
  if (!isInternalAgency(agency)) return "customer";
  return agency.internal_source === "auto" ? "prospect" : "internal";
};

export const isProspectAgency = (agency: InternalFields) =>
  agencyInternalState(agency) === "prospect";

/** Our own test studios: the only rows the default "Hide internal" hides. */
export const isHiddenInternalAgency = (agency: InternalFields) =>
  agencyInternalState(agency) === "internal";

/**
 * The Agencies list with the "Hide internal" filter applied. Only studios a
 * person marked internal (our test studios) hide; prospect studios stay in
 * view with their badge, so Jonny's prospects are never lost (plan G4).
 */
export const visibleAgencies = <T extends InternalFields>(
  agencies: readonly T[],
  hideInternal: boolean,
): T[] =>
  hideInternal
    ? agencies.filter((agency) => !isHiddenInternalAgency(agency))
    : [...agencies];

export const PROSPECT_BADGE_LABEL =
  "Prospect — internal until the client signs in";

export const PROSPECT_BADGE_TITLE =
  "A studio staff set up: " +
  INTERNAL_FLAG_EFFECT +
  ". It turns external by itself when a client login first signs in.";

export const INTERNAL_SWITCH_TITLE =
  `Internal: ${INTERNAL_FLAG_EFFECT}. ` +
  "Studios staff set up start internal and turn external when the client " +
  "first signs in; setting this switch by hand overrides that for good.";

/**
 * What the Internal switch writes. A person's choice is recorded as
 * `internal_source = 'manual'`, which the database never flips. Rows read
 * before 20260926120000_agency_internal_auto have no such column, so it is
 * only sent when the row has it (the switch keeps working before the apply).
 */
export const internalToggleData = (
  agency: Partial<InternalFields>,
  next: boolean,
): Pick<Agency, "is_internal" | "internal_source"> =>
  "internal_source" in agency
    ? { is_internal: next, internal_source: "manual" }
    : { is_internal: next };

export const internalToggleMessage = (next: boolean): string =>
  next
    ? `Marked internal — ${INTERNAL_FLAG_EFFECT}.`
    : "Marked as a customer agency — its films count as the customer's again and its alerts are no longer tagged internal.";

/** "Handed over to the client on 26 Sep 2026", or null when never handed over. */
export const handedOverLabel = (
  agency: Pick<Agency, "handed_over_at">,
  locale?: string,
): string | null => {
  if (!agency.handed_over_at) return null;
  const at = new Date(agency.handed_over_at);
  if (Number.isNaN(at.getTime())) return null;
  const day = new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(at);
  return `Handed over to the client on ${day}`;
};
